import { MiniKit } from '@worldcoin/minikit-js';
import { ethers } from 'ethers';
import {
  ON_CHAIN_TOKEN_ADDRESSES,
  type OnChainTokenSymbol,
  worldChainProvider,
} from './blockchain';
import { getMetaMaskSigner } from './metamask';
import { getPermit2ApprovalExpiration } from './permit2';
import {
  sendWorldTransaction,
  type WorldCalldataTransaction,
  type WorldTransactionNotice,
} from './worldMiniKit';

export const WORLD_CHAIN_SWAP_CONTRACT_ADDRESS = import.meta.env.VITE_WORLD_CHAIN_SWAP_CONTRACT_ADDRESS
  || '0xf032fad659BC4d982E29e52EABA80c186a3A1092';
export const PERMIT2_ALLOWANCE_ADDRESS = import.meta.env.VITE_PERMIT2_ADDRESS
  || '0x000000000022D473030F116dDEE9F6B43aC78BA3';

export const SWAP_SAFETY_BUFFER_BPS = 300;
export const SWAP_DEADLINE_SECONDS = 20 * 60;
const MAX_UINT160 = (1n << 160n) - 1n;

// Keep this matrix aligned with the currently verified World Chain pools/routes.
// BBG has no supported pool; BBT and GGMOMOMO only route directly through WLD.
export const SUPPORTED_SWAP_PAIRS: ReadonlyArray<readonly [OnChainTokenSymbol, OnChainTokenSymbol]> = [
  ['WLD', 'BBT'],
  ['WLD', 'GGMOMOMO'],
];

const SUPPORTED_SWAP_PAIR_KEYS = new Set(
  SUPPORTED_SWAP_PAIRS.map(([tokenA, tokenB]) => [tokenA, tokenB].sort().join(':')),
);

export function isSupportedSwapPair(
  tokenIn: OnChainTokenSymbol,
  tokenOut: OnChainTokenSymbol,
): boolean {
  return SUPPORTED_SWAP_PAIR_KEYS.has([tokenIn, tokenOut].sort().join(':'));
}

export function isSupportedSwapToken(token: OnChainTokenSymbol): boolean {
  return SUPPORTED_SWAP_PAIRS.some(([tokenIn, tokenOut]) => token === tokenIn || token === tokenOut);
}

export const ERC20_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'owner', type: 'address' },
      { internalType: 'address', name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint256', name: 'value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const;

export const PERMIT2_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'owner', type: 'address' },
      { internalType: 'address', name: 'token', type: 'address' },
      { internalType: 'address', name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [
      { internalType: 'uint160', name: 'amount', type: 'uint160' },
      { internalType: 'uint48', name: 'expiration', type: 'uint48' },
      { internalType: 'uint48', name: 'nonce', type: 'uint48' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'token', type: 'address' },
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint160', name: 'amount', type: 'uint160' },
      { internalType: 'uint48', name: 'expiration', type: 'uint48' },
    ],
    name: 'approve',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const;

export const SWAP_ABI = [
  {
    inputs: [],
    name: 'feeBps',
    outputs: [{ internalType: 'uint16', name: '', type: 'uint16' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
    ],
    name: 'quoteExactInput',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint256', name: 'amountOutMin', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
      { internalType: 'address', name: 'recipient', type: 'address' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
    ],
    name: 'swapExactInput',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
      { indexed: true, internalType: 'address', name: 'recipient', type: 'address' },
      { indexed: true, internalType: 'address', name: 'tokenIn', type: 'address' },
      { indexed: false, internalType: 'address', name: 'tokenOut', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'grossAmountIn', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'feeAmount', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'netAmountIn', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'amountOut', type: 'uint256' },
      { indexed: false, internalType: 'bytes32', name: 'routeHash', type: 'bytes32' },
    ],
    name: 'SwapExecuted',
    type: 'event',
  },
] as const;

const SWAP_INTERFACE = new ethers.Interface(SWAP_ABI);

export type SwapTokenRequest = {
  tokenIn: OnChainTokenSymbol;
  tokenOut: OnChainTokenSymbol;
  amount: string;
  walletAddress: string;
  rawBalance: bigint;
  decimals: number;
  onProgress?: (progress: SwapProgress) => void;
  transactionNotice?: WorldTransactionNotice;
};

export type SwapProgress =
  | 'checking'
  | 'approval'
  | 'approval-and-swap'
  | 'quoting'
  | 'swap'
  | 'confirming';

export type SwapQuote = {
  amountIn: bigint;
  quotedAmountOut: bigint;
  minimumAmountOut: bigint;
  feeBps: number;
  safetyBufferBps: number;
};

export type SwapResult = {
  approvalTxHashes: string[];
  txHash: string;
  userOpHash: string | null;
  quote: SwapQuote;
};

function getMiniKitWalletAddress(fallbackAddress: string): string {
  const candidate = MiniKit.user?.walletAddress || fallbackAddress;
  if (!ethers.isAddress(candidate)) {
    throw new Error('目前沒有有效的 World Chain 錢包地址。');
  }
  return ethers.getAddress(candidate);
}

function getPath(tokenIn: OnChainTokenSymbol, tokenOut: OnChainTokenSymbol): string[] {
  if (!isSupportedSwapPair(tokenIn, tokenOut)) {
    throw new Error('目前只支援 WLD ↔ BBT 及 WLD ↔ GGMOMOMO 兌換；BBG 及 BBT ↔ GGMOMOMO 暫未開放。');
  }
  const path = [ON_CHAIN_TOKEN_ADDRESSES[tokenIn], ON_CHAIN_TOKEN_ADDRESSES[tokenOut]];
  if (path[0].toLowerCase() === path[1].toLowerCase()) {
    throw new Error('輸入及輸出代幣不能相同。');
  }
  return path;
}

function parseAmount(amount: string, decimals: number, rawBalance: bigint): bigint {
  let amountIn: bigint;
  try {
    amountIn = ethers.parseUnits(amount.trim(), decimals);
  } catch {
    throw new Error(`數量最多支援 ${decimals} 位小數。`);
  }

  if (amountIn <= 0n) throw new Error('兌換數量必須大於 0。');
  if (amountIn > rawBalance) throw new Error('兌換數量不可超過目前鏈上餘額。');
  if (amountIn > MAX_UINT160) throw new Error('兌換數量超過 Permit2 支援上限。');
  return amountIn;
}

function calculateMinimumAmountOut(quotedAmountOut: bigint): bigint {
  return (quotedAmountOut * BigInt(10_000 - SWAP_SAFETY_BUFFER_BPS)) / 10_000n;
}

function getSwapContract() {
  return new ethers.Contract(WORLD_CHAIN_SWAP_CONTRACT_ADDRESS, SWAP_ABI, worldChainProvider);
}

export async function getSwapQuote({
  tokenIn,
  tokenOut,
  amount,
  decimals,
  rawBalance,
}: Pick<SwapTokenRequest, 'tokenIn' | 'tokenOut' | 'amount' | 'decimals' | 'rawBalance'>): Promise<SwapQuote> {
  const amountIn = parseAmount(amount, decimals, rawBalance);
  const path = getPath(tokenIn, tokenOut);
  const swapContract = getSwapContract();
  const amounts = await swapContract.quoteExactInput(amountIn, path) as bigint[];
  const feeBps = Number(await swapContract.feeBps());
  const quotedAmountOut = amounts[amounts.length - 1];

  if (!quotedAmountOut || quotedAmountOut <= 0n) {
    throw new Error('目前無法取得有效報價，請稍後再試。');
  }

  const minimumAmountOut = calculateMinimumAmountOut(quotedAmountOut);
  if (minimumAmountOut <= 0n) throw new Error('最低輸出數量無效，請調低兌換數量後再試。');

  return {
    amountIn,
    quotedAmountOut,
    minimumAmountOut,
    feeBps,
    safetyBufferBps: SWAP_SAFETY_BUFFER_BPS,
  };
}

async function getPermit2Allowance(owner: string, token: string): Promise<{ amount: bigint; expiration: bigint }> {
  const contract = new ethers.Contract(PERMIT2_ALLOWANCE_ADDRESS, PERMIT2_ABI, worldChainProvider);
  const result = await contract.allowance(owner, token, WORLD_CHAIN_SWAP_CONTRACT_ADDRESS) as [bigint, bigint, bigint];
  return { amount: result[0], expiration: result[1] };
}

export async function executeSwapWithWorldApp(request: SwapTokenRequest): Promise<SwapResult> {
  if (!MiniKit.isInstalled()) {
    throw new Error('請在 World App 內開啟 Bubble Wallet 後再進行兌換。');
  }
  const owner = getMiniKitWalletAddress(request.walletAddress);
  const tokenAddress = ON_CHAIN_TOKEN_ADDRESSES[request.tokenIn];
  const path = getPath(request.tokenIn, request.tokenOut);
  const amountIn = parseAmount(request.amount, request.decimals, request.rawBalance);

  request.onProgress?.('checking');
  const permit2Allowance = await getPermit2Allowance(owner, tokenAddress);
  const now = BigInt(Math.floor(Date.now() / 1000));
  const needsPermit2Approval = permit2Allowance.amount < amountIn || permit2Allowance.expiration <= now;

  request.onProgress?.('quoting');
  const quote = await getSwapQuote({
    tokenIn: request.tokenIn,
    tokenOut: request.tokenOut,
    amount: request.amount,
    decimals: request.decimals,
    rawBalance: request.rawBalance,
  });

  const deadline = Math.floor(Date.now() / 1000) + SWAP_DEADLINE_SECONDS;
  const transactions: WorldCalldataTransaction[] = [];

  // Keep the Permit2 approval and swap in one MiniKit operation so the allowance
  // is created and consumed in the same user operation.
  if (needsPermit2Approval) {
    transactions.push({
      to: PERMIT2_ALLOWANCE_ADDRESS,
      data: new ethers.Interface(PERMIT2_ABI).encodeFunctionData('approve', [
        tokenAddress,
        WORLD_CHAIN_SWAP_CONTRACT_ADDRESS,
        amountIn,
        0n,
      ]),
    });
  }

  transactions.push({
    to: WORLD_CHAIN_SWAP_CONTRACT_ADDRESS,
    data: SWAP_INTERFACE.encodeFunctionData('swapExactInput', [
      quote.amountIn.toString(),
      quote.minimumAmountOut.toString(),
      path,
      owner,
      deadline.toString(),
    ]),
  });

  request.onProgress?.(needsPermit2Approval ? 'approval-and-swap' : 'swap');
  const { userOpHash, txHash } = await sendWorldTransaction(
    transactions,
    () => request.onProgress?.('confirming'),
    request.transactionNotice,
  );

  return { approvalTxHashes: [], txHash, userOpHash, quote };
}

export async function executeSwapWithMetaMask(request: SwapTokenRequest): Promise<SwapResult> {
  const { address: owner, provider, signer } = await getMetaMaskSigner();
  if (!ethers.isAddress(request.walletAddress) || owner.toLowerCase() !== request.walletAddress.toLowerCase()) {
    throw new Error('MetaMask 帳戶已變更，請重新連接並重新取得餘額。');
  }
  const tokenAddress = ON_CHAIN_TOKEN_ADDRESSES[request.tokenIn];
  const path = getPath(request.tokenIn, request.tokenOut);
  const amountIn = parseAmount(request.amount, request.decimals, request.rawBalance);
  const approvalTxHashes: string[] = [];

  request.onProgress?.('checking');
  const erc20ReadContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
  const erc20Allowance = await erc20ReadContract.allowance(owner, PERMIT2_ALLOWANCE_ADDRESS) as bigint;
  if (erc20Allowance < amountIn) {
    request.onProgress?.('approval');
    const erc20WriteContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const approvalTransaction = await erc20WriteContract.approve(PERMIT2_ALLOWANCE_ADDRESS, amountIn);
    approvalTxHashes.push(approvalTransaction.hash);
    const receipt = await approvalTransaction.wait();
    if (!receipt || receipt.status !== 1) throw new Error('ERC-20 授權交易未成功。');
  }

  const permit2ReadContract = new ethers.Contract(PERMIT2_ALLOWANCE_ADDRESS, PERMIT2_ABI, provider);
  const permit2Allowance = await permit2ReadContract.allowance(owner, tokenAddress, WORLD_CHAIN_SWAP_CONTRACT_ADDRESS) as [bigint, bigint, bigint];
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (permit2Allowance[0] < amountIn || permit2Allowance[1] <= now) {
    request.onProgress?.('approval');
    const expiration = getPermit2ApprovalExpiration(now);
    const permit2WriteContract = new ethers.Contract(PERMIT2_ALLOWANCE_ADDRESS, PERMIT2_ABI, signer);
    const approvalTransaction = await permit2WriteContract.approve(
      tokenAddress,
      WORLD_CHAIN_SWAP_CONTRACT_ADDRESS,
      amountIn,
      expiration,
    );
    approvalTxHashes.push(approvalTransaction.hash);
    const receipt = await approvalTransaction.wait();
    if (!receipt || receipt.status !== 1) throw new Error('Permit2 授權交易未成功。');
  }

  request.onProgress?.('quoting');
  const quote = await getSwapQuote({
    tokenIn: request.tokenIn,
    tokenOut: request.tokenOut,
    amount: request.amount,
    decimals: request.decimals,
    rawBalance: request.rawBalance,
  });

  request.onProgress?.('swap');
  const deadline = Math.floor(Date.now() / 1000) + SWAP_DEADLINE_SECONDS;
  const swapWriteContract = new ethers.Contract(WORLD_CHAIN_SWAP_CONTRACT_ADDRESS, SWAP_ABI, signer);
  const swapTransaction = await swapWriteContract.swapExactInput(
    quote.amountIn,
    quote.minimumAmountOut,
    path,
    owner,
    deadline,
  );

  return {
    approvalTxHashes,
    txHash: swapTransaction.hash,
    userOpHash: null,
    quote,
  };
}

export function formatSwapAmount(rawAmount: bigint, decimals: number, fractionDigits = 6): string {
  const formatted = ethers.formatUnits(rawAmount, decimals);
  const [integerPart, fractionPart] = formatted.split('.');
  const trimmedFraction = (fractionPart ?? '').slice(0, fractionDigits).replace(/0+$/, '');
  return trimmedFraction ? `${integerPart}.${trimmedFraction}` : integerPart;
}

export function getSwapWorldScanUrl(txHash: string): string {
  return `https://worldscan.org/tx/${txHash}`;
}

export async function getConfirmedSwapOutput(txHash: string): Promise<bigint | null> {
  const receipt = await worldChainProvider.getTransactionReceipt(txHash);
  if (!receipt) return null;

  for (const log of receipt.logs) {
    try {
      const parsed = SWAP_INTERFACE.parseLog({ topics: log.topics, data: log.data });
      if (parsed?.name !== 'SwapExecuted') continue;
      const amountOut = parsed.args.amountOut;
      return typeof amountOut === 'bigint' ? amountOut : BigInt(amountOut.toString());
    } catch {
      // Other logs belong to Permit2, the router, or the token contracts.
    }
  }

  return null;
}
